/* sl-converge.js — flush lettering, scroll until lock, then rest */
(function () {
  'use strict';

  if (window.SLConverge) return;

  var instances = new WeakMap();
  var MOBILE_QUERY = '(max-width: 767px)';
  var FONT = {
    sans: 'var(--font-sans)',
    serif: 'var(--font-serif)',
    'serif-cond': 'var(--font-serif-cond)',
    'serif-extra': 'var(--font-serif-extra)',
    display: 'var(--font-display)',
    mono: 'var(--font-mono)'
  };

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function isMobile() {
    return window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
  }

  function parseJsonAttr(value, fallback) {
    if (value == null || value === '') return fallback;
    try {
      return JSON.parse(value);
    } catch (error) {
      return fallback;
    }
  }

  function numberAttr(root, name, fallback) {
    var value = parseFloat(root.getAttribute(name));
    return isFinite(value) ? value : fallback;
  }

  function cssNumber(root, name, fallback) {
    var raw = getComputedStyle(root).getPropertyValue(name).trim();
    var value = parseFloat(raw);
    return isFinite(value) ? value : fallback;
  }

  function sectionRoot(event) {
    if (!event || !event.target) return null;
    if (event.target.matches && event.target.matches('[data-sl-converge]')) return event.target;
    if (event.target.querySelector) {
      var descendant = event.target.querySelector('[data-sl-converge]');
      if (descendant) return descendant;
    }
    return event.target.closest ? event.target.closest('[data-sl-converge]') : null;
  }

  function Converge(root) {
    this.root = root;
    this.host = root.closest('.shopify-section');
    this.stack = root.querySelector('[data-sl-converge-stack]');
    this.stage = root.querySelector('.sl-converge__stage');
    this.subheadline = root.querySelector('[data-sl-converge-subheadline]');
    this.probe = null;
    this.splitCache = {};
    this.measureCache = {};
    this.context = null;
    this.timeline = null;
    this.pinPhase = null;
    this.motionRefreshTimer = 0;
    this.resizeTimer = 0;
    this.destroyed = false;
    this.boundResize = this.onResize.bind(this);
    this.media = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;
    this.motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    this.boundMedia = this.refresh.bind(this);
    this.createProbe();
    this.refresh();
    window.addEventListener('resize', this.boundResize);
    if (window.ResizeObserver && this.stage) {
      var self = this;
      var observedWidth = this.stage.getBoundingClientRect().width;
      this.resizeObserver = new ResizeObserver(function () {
        var width = self.stage.getBoundingClientRect().width;
        if (Math.abs(width - observedWidth) < 0.5) return;
        observedWidth = width;
        self.onResize();
      });
      this.resizeObserver.observe(this.stage);
    }
    if (this.media && this.media.addEventListener) this.media.addEventListener('change', this.boundMedia);
    if (this.motionQuery && this.motionQuery.addEventListener) this.motionQuery.addEventListener('change', this.boundMedia);
  }

  Converge.prototype.createProbe = function () {
    var probe = document.createElement('span');
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = 'position:fixed;left:-99999px;top:0;white-space:nowrap;font-size:100px;pointer-events:none;visibility:hidden;';
    document.body.appendChild(probe);
    this.probe = probe;
  };

  Converge.prototype.phrase = function () {
    return String(parseJsonAttr(this.root.getAttribute('data-phrase'), '') || '').replace(/\s+/g, ' ').trim();
  };

  Converge.prototype.highlightTokens = function () {
    var nodes = this.root.querySelectorAll('[data-sl-converge-highlights] .c2o3-text-token');
    return Array.prototype.map.call(nodes, function (token) {
      return {
        target: token.getAttribute('data-target') || '',
        font: token.getAttribute('data-font') || 'inherit',
        size: parseFloat(token.getAttribute('data-size')) || 0,
        sizeMobile: parseFloat(token.getAttribute('data-size-mobile')) || 0,
        leading: parseFloat(token.getAttribute('data-leading')) || 0,
        leadingMobile: parseFloat(token.getAttribute('data-leading-mobile')) || 0,
        weight: parseInt(token.getAttribute('data-weight'), 10) || 0,
        tracking: parseInt(token.getAttribute('data-tracking'), 10) || 0,
        italic: token.getAttribute('data-italic') === 'true',
        uppercase: token.getAttribute('data-uppercase') === 'true',
        color: token.getAttribute('data-color') || ''
      };
    }).filter(function (token) { return token.target; });
  };

  Converge.prototype.measure = function (text) {
    var probe = this.probe;
    if (!probe) return 0;
    if (Object.prototype.hasOwnProperty.call(this.measureCache, text)) return this.measureCache[text];
    if (probe.textContent !== text) probe.textContent = text;
    var width = probe.getBoundingClientRect().width;
    this.measureCache[text] = width;
    return width;
  };

  Converge.prototype.prepareMeasure = function () {
    var probe = this.probe;
    var stack = this.stack;
    if (!probe || !stack) return;
    var styles = getComputedStyle(stack);
    probe.style.fontFamily = styles.fontFamily;
    probe.style.fontWeight = styles.fontWeight;
    probe.style.fontStyle = styles.fontStyle;
    probe.style.letterSpacing = styles.letterSpacing;
    probe.style.textTransform = styles.textTransform;
  };

  Converge.prototype.split = function (words, count) {
    var cacheKey = words.join('\u0001') + '::' + count;
    if (this.splitCache[cacheKey]) return this.splitCache[cacheKey];
    var total = words.length;
    if (count > total) return null;

    var widths = words.map(function (word) {
      return this.measure(word);
    }, this);
    var space = this.measure('n n') - this.measure('nn');
    var prefix = [0];
    widths.forEach(function (width, index) {
      prefix.push(prefix[index] + width);
    });

    function spanWidth(start, end) {
      return prefix[end] - prefix[start] + space * (end - start - 1);
    }

    var average = spanWidth(0, total) / count;
    function cost(start, end) {
      var delta = spanWidth(start, end) - average;
      return delta * delta;
    }

    var dp = [];
    var from = [];
    var k;
    var j;
    var i;
    var value;
    for (k = 0; k <= count; k += 1) {
      dp.push(new Array(total + 1).fill(Infinity));
      from.push(new Array(total + 1).fill(-1));
    }
    dp[0][0] = 0;
    for (k = 1; k <= count; k += 1) {
      for (j = k; j <= total - (count - k); j += 1) {
        for (i = k - 1; i < j; i += 1) {
          value = dp[k - 1][i] + cost(i, j);
          if (value < dp[k][j]) {
            dp[k][j] = value;
            from[k][j] = i;
          }
        }
      }
    }
    if (!isFinite(dp[count][total])) return null;

    var lines = [];
    j = total;
    for (k = count; k > 0; k -= 1) {
      i = from[k][j];
      lines.unshift(words.slice(i, j).join(' '));
      j = i;
    }
    this.splitCache[cacheKey] = lines;
    return lines;
  };

  Converge.prototype.fitBlock = function (lines, box) {
    var sizes = lines.map(function (line) {
      var width = this.measure(line) || 1;
      return 100 * box / width;
    }, this);
    var lineHeight = cssNumber(this.root, '--sl-converge-lh', 0.84);
    var total = lineHeight * sizes.reduce(function (sum, size) {
      return sum + size;
    }, 0);
    var max = Math.max.apply(Math, sizes);
    var min = Math.min.apply(Math, sizes);
    return { sizes: sizes, total: total, ratio: min ? max / min : 1, lineHeight: lineHeight };
  };

  Converge.prototype.applyHighlightStyle = function (mark, token) {
    if (FONT[token.font]) mark.style.setProperty('--sl-converge-mark-font', FONT[token.font]);
    if (token.size > 0) mark.style.setProperty('--sl-converge-mark-size-desktop', token.size + 'px');
    if (token.sizeMobile > 0) mark.style.setProperty('--sl-converge-mark-size-mobile', token.sizeMobile + 'px');
    if (token.leading > 0) mark.style.setProperty('--sl-converge-mark-leading-desktop', String(token.leading));
    if (token.leadingMobile > 0) mark.style.setProperty('--sl-converge-mark-leading-mobile', String(token.leadingMobile));
    if (token.font === 'display') {
      mark.style.setProperty('--sl-converge-mark-weight', '700');
      mark.style.setProperty('--sl-converge-mark-tracking', '0');
    } else {
      if (token.weight > 0) mark.style.setProperty('--sl-converge-mark-weight', String(token.weight));
      if (token.tracking !== 0) mark.style.setProperty('--sl-converge-mark-tracking', token.tracking / 1000 + 'em');
    }
    if (token.font === 'serif-extra' || token.italic) mark.style.setProperty('--sl-converge-mark-style', 'italic');
    if (token.uppercase) mark.style.setProperty('--sl-converge-mark-transform', 'uppercase');
    if (token.color && token.color !== 'rgba(0,0,0,0)') mark.style.setProperty('--sl-converge-mark-color', token.color);
  };

  Converge.prototype.highlightMatches = function (text, tokens) {
    var matches = [];
    tokens.forEach(function (token, tokenIndex) {
      var from = 0;
      var found;
      while ((found = text.indexOf(token.target, from)) !== -1) {
        matches.push({ start: found, end: found + token.target.length, token: token, order: tokenIndex });
        from = found + token.target.length;
      }
    });
    matches.sort(function (a, b) { return a.start - b.start || a.order - b.order; });

    var cursor = 0;
    return matches.filter(function (match) {
      if (match.start < cursor) return false;
      cursor = match.end;
      return true;
    });
  };

  Converge.prototype.appendHighlightedText = function (line, text, matches) {
    var cursor = 0;
    matches.forEach(function (match) {
      if (match.start > cursor) line.appendChild(document.createTextNode(text.slice(cursor, match.start)));
      var mark = document.createElement('span');
      mark.className = 'sl-converge__mark';
      mark.textContent = text.slice(match.start, match.end);
      this.applyHighlightStyle(mark, match.token);
      line.appendChild(mark);
      cursor = match.end;
    }, this);
    if (cursor < text.length) line.appendChild(document.createTextNode(text.slice(cursor)));
  };

  Converge.prototype.renderLines = function (lines, tokens) {
    this.stack.textContent = '';
    var source = lines.join(' ');
    var matches = this.highlightMatches(source, tokens);
    var lineStart = 0;
    lines.forEach(function (text) {
      var row = document.createElement('div');
      var line = document.createElement('span');
      var lineEnd = lineStart + text.length;
      var localMatches = matches.filter(function (match) {
        return match.end > lineStart && match.start < lineEnd;
      }).map(function (match) {
        return {
          start: Math.max(match.start, lineStart) - lineStart,
          end: Math.min(match.end, lineEnd) - lineStart,
          token: match.token,
          order: match.order
        };
      });
      row.className = 'sl-converge__row';
      line.className = 'sl-converge__line';
      this.appendHighlightedText(line, text, localMatches);
      row.appendChild(line);
      this.stack.appendChild(row);
      lineStart = lineEnd + 1;
    }, this);
  };

  Converge.prototype.layout = function () {
    var root = this.root;
    var stack = this.stack;
    var stage = this.stage;
    if (!stack || !stage) return;

    var rootStyles = getComputedStyle(root);
    [
      '--sl-converge-bg', '--sl-converge-color', '--sl-converge-font',
      '--sl-converge-weight', '--sl-converge-style', '--sl-converge-tracking',
      '--sl-converge-width', '--sl-converge-scale', '--sl-converge-fill',
      '--sl-converge-stage-height', '--sl-converge-pad-top', '--sl-converge-pad-right',
      '--sl-converge-pad-bottom', '--sl-converge-pad-left', '--sl-converge-runway',
      '--sl-converge-lh', '--sl-converge-header', '--sl-converge-sticky-gap'
    ].forEach(function (name) {
      stage.style.setProperty(name, rootStyles.getPropertyValue(name));
    });

    var phrase = this.phrase();
    var box = stack.getBoundingClientRect().width;
    if (!phrase || !box) return;
    this.prepareMeasure();

    var stageStyles = getComputedStyle(stage);
    var paddingTop = parseFloat(stageStyles.paddingTop) || 0;
    var paddingBottom = parseFloat(stageStyles.paddingBottom) || 0;
    var subheadlineHeight = this.subheadline ? this.subheadline.getBoundingClientRect().height : 0;
    var contentGap = this.subheadline ? parseFloat(getComputedStyle(this.subheadline).marginBottom) || 0 : 0;
    var fill = cssNumber(root, '--sl-converge-fill', 0.82);
    var avail = Math.max(80, fill * (stage.clientHeight - paddingTop - paddingBottom - subheadlineHeight - contentGap));
    var mobile = isMobile();
    var maxLines = Math.max(1, Math.round(numberAttr(root, mobile ? 'data-max-lines-mobile' : 'data-max-lines-desktop', 6)));
    var minLines = Math.max(1, Math.round(numberAttr(root, mobile ? 'data-min-lines-mobile' : 'data-min-lines-desktop', 1)));
    minLines = Math.min(minLines, maxLines);
    var evenness = numberAttr(root, 'data-evenness', 4) / 10;
    var scale = cssNumber(root, '--sl-converge-scale', 1);
    var hardLines = String(parseJsonAttr(root.getAttribute('data-phrase'), '') || '')
      .split(/\n/)
      .map(function (line) { return line.replace(/\s+/g, ' ').trim(); })
      .filter(Boolean);

    var best = null;
    if (hardLines.length > 1) {
      best = { lines: hardLines, fit: this.fitBlock(hardLines, box) };
    } else {
      var words = phrase.split(' ').filter(Boolean);
      var count;
      var lines;
      var fit;
      var score;
      for (count = Math.min(minLines, words.length); count <= Math.min(maxLines, words.length); count += 1) {
        lines = this.split(words, count);
        if (!lines) continue;
        fit = this.fitBlock(lines, box);
        score = fit.total / Math.pow(fit.ratio, 2 * evenness);
        if (!best || score > best.score) best = { lines: lines, fit: fit, score: score };
      }
      if (!best) {
        lines = this.split(words, Math.min(maxLines, words.length)) || [phrase];
        best = { lines: lines, fit: this.fitBlock(lines, box) };
      }
    }

    var sizes = best.fit.sizes.slice();
    var total = best.fit.total * scale;
    if (total > avail && total > 0) {
      scale *= avail / total;
      total = avail;
    }
    sizes = sizes.map(function (size) { return size * scale; });

    var tokens = this.highlightTokens();
    var signature = best.lines.join('|') + '::' + JSON.stringify(tokens);
    if (stack.dataset.sig !== signature) {
      stack.dataset.sig = signature;
      this.renderLines(best.lines, tokens);
    }

    var nodes = stack.querySelectorAll('.sl-converge__line');
    var leading = best.fit.lineHeight;
    for (var index = 0; index < nodes.length; index += 1) {
      nodes[index].style.fontSize = sizes[index].toFixed(2) + 'px';
      nodes[index].style.lineHeight = leading.toFixed(3);
      nodes[index].parentElement.style.lineHeight = leading.toFixed(3);
    }

    root.classList.add('is-ready');
    stage.classList.add('is-ready');
    this.buildMotion();
  };

  Converge.prototype.killMotion = function () {
    window.clearTimeout(this.motionRefreshTimer);
    this.motionRefreshTimer = 0;
    this.clearMobilePin();
    if (this.timeline && this.timeline.scrollTrigger) this.timeline.scrollTrigger.kill();
    if (this.timeline) this.timeline.kill();
    this.timeline = null;
    if (this.context) {
      this.context.revert();
      this.context = null;
    }
  };

  Converge.prototype.clearMobilePin = function () {
    this.root.classList.remove('is-scroll-pinned', 'is-scroll-past');
    this.pinPhase = null;
    if (this.stage) {
      this.stage.style.removeProperty('left');
      this.stage.style.removeProperty('width');
      this.stage.style.removeProperty('transform');
    }
    if (this.host) this.host.classList.remove('sl-converge-scroll-host');
  };

  Converge.prototype.setPinPhase = function (phase) {
    if (!isMobile() || !this.stage) {
      this.clearMobilePin();
      return;
    }
    if (this.pinPhase === phase) return;

    var pinned = phase === 'pinned';
    var past = phase === 'past';
    if (pinned) {
      var rootRect = this.root.getBoundingClientRect();
      this.stage.style.left = rootRect.left + 'px';
      this.stage.style.width = rootRect.width + 'px';
    } else {
      this.stage.style.removeProperty('left');
      this.stage.style.removeProperty('width');
    }

    /* Concept applies content-visibility:auto to later sections. While pinned,
       lift only this host so the fixed stage belongs to the viewport compositor;
       restore the theme optimization as soon as the scene is released. */
    if (this.host) this.host.classList.toggle('sl-converge-scroll-host', pinned);
    this.root.classList.toggle('is-scroll-pinned', pinned);
    this.root.classList.toggle('is-scroll-past', past);
    this.pinPhase = phase;
  };

  Converge.prototype.syncMobilePin = function (trigger) {
    if (!isMobile() || !trigger) {
      this.clearMobilePin();
      return;
    }
    var scroll = window.scrollY;
    var phase = scroll < trigger.start ? 'before' : (scroll >= trigger.end ? 'past' : 'pinned');
    this.setPinPhase(phase);
  };

  Converge.prototype.setFinal = function () {
    var lines = this.stack ? this.stack.querySelectorAll('.sl-converge__line') : [];
    var stage = this.stage;
    var subheadline = this.subheadline;
    this.killMotion();
    this.root.classList.add('is-static');
    if (window.gsap && lines.length) {
      window.gsap.set(lines, { xPercent: 0, opacity: 1, clearProps: 'transform' });
      if (stage) window.gsap.set(stage, { clearProps: 'transform' });
      if (subheadline) window.gsap.set(subheadline, { x: 0, y: 0, opacity: 1, clearProps: 'transform' });
    } else {
      for (var index = 0; index < lines.length; index += 1) {
        lines[index].style.transform = 'none';
        lines[index].style.opacity = '1';
      }
      if (subheadline) {
        subheadline.style.transform = 'none';
        subheadline.style.opacity = '1';
      }
      if (stage) stage.style.transform = 'none';
    }
  };

  Converge.prototype.buildMotion = function () {
    var root = this.root;
    var stack = this.stack;
    var subheadline = this.subheadline;
    if (!stack) return;

    var lines = stack.querySelectorAll('.sl-converge__line');
    var motionEnabled = root.getAttribute('data-motion') !== 'false';

    if (
      isDesignMode()
      || !motionEnabled
      || prefersReducedMotion()
      || !window.gsap
      || !window.ScrollTrigger
      || !lines.length
    ) {
      this.setFinal();
      return;
    }

    window.gsap.registerPlugin(window.ScrollTrigger);
    this.killMotion();
    root.classList.remove('is-static');

    var distance = numberAttr(root, 'data-parallax', 18);
    var scrub = numberAttr(root, 'data-scrub', 6) / 30;
    var mobile = isMobile();
    var subheadlineMotion = subheadline && subheadline.getAttribute('data-subheadline-motion') !== 'false';
    var subheadlineStyle = subheadline ? subheadline.getAttribute('data-subheadline-motion-style') || 'fade-up' : 'fade-up';
    var subheadlinePhase = subheadlineMotion ? numberAttr(subheadline, 'data-subheadline-motion-phase', 0.3) : 0;
    var subheadlineDistance = subheadline
      ? cssNumber(subheadline, mobile ? '--sl-converge-sub-distance-mobile' : '--sl-converge-sub-distance-desktop', 12)
      : 0;
    var subheadlineFrom = { x: 0, y: 0, opacity: 0 };
    if (subheadlineStyle === 'fade-up') subheadlineFrom.y = subheadlineDistance;
    if (subheadlineStyle === 'fade-down') subheadlineFrom.y = -subheadlineDistance;
    if (subheadlineStyle === 'fade-left') subheadlineFrom.x = -subheadlineDistance;
    if (subheadlineStyle === 'fade-right') subheadlineFrom.x = subheadlineDistance;
    var fastScrollEnd = true;
    var stagger = 0.08;

    if (mobile) {
      scrub = Math.min(scrub, 0.18);
      stagger = 0.04;
    }

    var self = this;

    this.context = window.gsap.context(function () {
      window.gsap.set(lines, {
        xPercent: function (index) {
          return (index % 2 ? 1 : -1) * distance;
        },
        opacity: 0
      });
      if (subheadlineMotion) {
        window.gsap.set(subheadline, subheadlineFrom);
      } else if (subheadline) {
        window.gsap.set(subheadline, { x: 0, y: 0, opacity: 1, clearProps: 'transform' });
      }

      self.timeline = window.gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: root,
          start: function () {
            var header = cssNumber(root, '--sl-converge-header', 0);
            return 'top top+=' + Math.max(0, header);
          },
          end: function () {
            var stageHeight = self.stage ? self.stage.getBoundingClientRect().height : 0;
            return '+=' + Math.max(1, root.getBoundingClientRect().height - stageHeight);
          },
          scrub: scrub,
          fastScrollEnd: fastScrollEnd,
          invalidateOnRefresh: true,
          onUpdate: function (trigger) {
            if (mobile) self.syncMobilePin(trigger);
          },
          onRefresh: function (trigger) {
            if (mobile) self.syncMobilePin(trigger);
          }
        }
      });

      if (subheadlineMotion) {
        self.timeline.to(subheadline, { x: 0, y: 0, opacity: 1, duration: subheadlinePhase }, 0);
      }

      self.timeline.to(
        lines,
        {
          xPercent: 0,
          opacity: 1,
          duration: 1,
          stagger: stagger
        },
        subheadlinePhase
      );
    }, root);

    var trigger = this.timeline && this.timeline.scrollTrigger;
    if (trigger) {
      this.motionRefreshTimer = window.setTimeout(function () {
        self.motionRefreshTimer = 0;
        if (!self.destroyed && self.timeline && self.timeline.scrollTrigger === trigger) trigger.refresh();
      }, 120);
    }
  };

  Converge.prototype.refresh = function () {
    if (this.destroyed) return;
    this.splitCache = {};
    this.measureCache = {};
    if (this.stack) delete this.stack.dataset.sig;
    this.layout();
  };

  Converge.prototype.onResize = function () {
    var self = this;
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(function () {
      self.refresh();
    }, 80);
  };

  Converge.prototype.select = function () {
    this.refresh();
  };

  Converge.prototype.deselect = function () {
    if (isDesignMode()) this.setFinal();
  };

  Converge.prototype.destroy = function () {
    this.destroyed = true;
    window.clearTimeout(this.resizeTimer);
    window.removeEventListener('resize', this.boundResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.media && this.media.removeEventListener) this.media.removeEventListener('change', this.boundMedia);
    if (this.motionQuery && this.motionQuery.removeEventListener) this.motionQuery.removeEventListener('change', this.boundMedia);
    this.killMotion();
    if (this.probe && this.probe.parentNode) this.probe.parentNode.removeChild(this.probe);
    this.probe = null;
    instances.delete(this.root);
  };

  function boot(root, refreshExisting) {
    if (!root) return null;
    if (instances.has(root)) {
      if (refreshExisting) instances.get(root).refresh();
      return instances.get(root);
    }
    var instance = new Converge(root);
    instances.set(root, instance);
    return instance;
  }

  function bootAll(scope, refreshExisting) {
    var roots = (scope || document).querySelectorAll('[data-sl-converge]');
    for (var index = 0; index < roots.length; index += 1) boot(roots[index], refreshExisting);
  }

  function whenReady(callback) {
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(callback).catch(callback);
      return;
    }
    callback();
  }

  window.SLConverge = { init: boot, initAll: bootAll };

  whenReady(function () { bootAll(document, true); });
  if (document.fonts && document.fonts.addEventListener) {
    document.fonts.addEventListener('loadingdone', function () { bootAll(document, true); });
  }
  document.addEventListener('DOMContentLoaded', function () { bootAll(document); });
  document.addEventListener('page:loaded', function () { bootAll(document); });
  window.addEventListener('pageshow', function (event) { bootAll(document, event.persisted); });
  document.addEventListener('shopify:section:load', function (event) {
    var root = sectionRoot(event);
    if (root) boot(root, true);
  });
  document.addEventListener('shopify:section:select', function (event) {
    var root = sectionRoot(event);
    var instance = root && boot(root);
    if (instance) instance.select();
  });
  document.addEventListener('shopify:section:deselect', function (event) {
    var root = sectionRoot(event);
    var instance = root && instances.get(root);
    if (instance) instance.deselect();
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var root = sectionRoot(event);
    var instance = root && instances.get(root);
    if (instance) instance.destroy();
  });
}());
